import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Send, Loader2, CheckCircle2, Mail, Facebook } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { trackEvent } from "../analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL = "Greg@ShipCR.com";
const FB = "https://www.facebook.com/ShipCostaRica/";

const Contact = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source: "contact-form" });
      trackEvent("submit_lead", { source: "contact-form" });
      setDone(true);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      setError(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 sm:py-32 bg-brand-navy text-brand-cream overflow-hidden"
    >
      <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-brand-red/15 blur-3xl" aria-hidden="true" />
      <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full bg-brand-blue/30 blur-3xl" aria-hidden="true" />

      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="text-center max-w-3xl mx-auto mb-12"
        >
          <div className="inline-flex items-center gap-2 mb-6 justify-center">
            <span className="h-px w-8 bg-brand-red" />
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-brand-red">
              {t.contact.eyebrow}
            </span>
            <span className="h-px w-8 bg-brand-red" />
          </div>
          <h2 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-[-0.02em] leading-[1.05]">
            {t.contact.title}
          </h2>
          <p className="mt-5 text-lg text-brand-cream/75 leading-relaxed">{t.contact.sub}</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-10 items-start">
          {/* Form */}
          <motion.form
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-3 rounded-3xl bg-brand-cream/8 backdrop-blur-md border border-brand-cream/15 p-7 sm:p-9"
          >
            {done ? (
              <div data-testid="contact-success" className="py-8 text-center">
                <CheckCircle2 size={48} className="text-brand-red mx-auto mb-4" />
                <div className="font-heading font-black text-2xl mb-2">{t.contact.thankYou}</div>
                <div className="text-brand-cream/75">{t.contact.thankYouSub}</div>
                <button
                  type="button"
                  onClick={() => setDone(false)}
                  className="mt-6 text-sm font-bold text-brand-red hover:text-brand-cream transition"
                  data-testid="contact-send-another"
                >
                  {t.contact.sendAnother}
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field
                    label={t.contact.name}
                    value={form.name}
                    onChange={change("name")}
                    required
                    testid="contact-input-name"
                  />
                  <Field
                    label={t.contact.email}
                    type="email"
                    value={form.email}
                    onChange={change("email")}
                    required
                    testid="contact-input-email"
                  />
                </div>
                <div className="mt-4">
                  <Field
                    label={t.contact.phone}
                    type="tel"
                    value={form.phone}
                    onChange={change("phone")}
                    testid="contact-input-phone"
                  />
                </div>
                <div className="mt-4">
                  <label className="block text-xs font-bold uppercase tracking-widest text-brand-cream/65 mb-2">
                    {t.contact.message}
                  </label>
                  <textarea
                    value={form.message}
                    onChange={change("message")}
                    rows={4}
                    data-testid="contact-input-message"
                    className="w-full rounded-2xl bg-brand-navy/40 border border-brand-cream/15 px-4 py-3 text-brand-cream placeholder-brand-cream/40 font-medium focus:outline-none focus:ring-2 focus:ring-brand-red/40 focus:border-brand-red/40 transition resize-none"
                  />
                </div>
                {error && (
                  <div data-testid="contact-error" className="mt-4 text-sm font-semibold text-brand-red bg-brand-red/10 border border-brand-red/20 rounded-xl px-4 py-2">
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="mt-6 group inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-brand-red text-white font-heading font-bold text-base px-7 py-3.5 hover:bg-brand-cream hover:text-brand-navy transition-all disabled:opacity-60"
                >
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                  {loading ? t.contact.sending : t.contact.send}
                </button>
              </>
            )}
          </motion.form>

          {/* Direct contact options */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-2 space-y-4"
          >
            <a
              href={`mailto:${EMAIL}`}
              data-testid="contact-email-btn"
              className="block rounded-2xl bg-brand-cream/8 backdrop-blur-md border border-brand-cream/15 hover:bg-brand-cream hover:text-brand-navy p-5 transition-all"
            >
              <Mail size={20} className="mb-2 text-brand-red" />
              <div className="text-[11px] uppercase tracking-widest font-bold opacity-65 mb-0.5">
                {t.contact.emailUs}
              </div>
              <div className="font-heading font-bold text-lg break-all">{EMAIL}</div>
            </a>
            <a
              href={FB}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-facebook-btn"
              className="block rounded-2xl bg-brand-cream/8 backdrop-blur-md border border-brand-cream/15 hover:bg-brand-cream hover:text-brand-navy p-5 transition-all"
            >
              <Facebook size={20} className="mb-2 text-brand-red" />
              <div className="text-[11px] uppercase tracking-widest font-bold opacity-65 mb-0.5">
                {t.contact.fbLabel}
              </div>
              <div className="font-heading font-bold text-lg">@ShipCostaRica</div>
            </a>
            <a
              id="create-profile"
              href="https://trackmypackage.app/join/shipcostarica" target="_blank" rel="noopener noreferrer"
              data-testid="contact-cta"
              className="block rounded-2xl bg-brand-red hover:bg-brand-cream hover:text-brand-navy text-white p-5 text-center transition-all"
            >
              <div className="font-heading font-black text-lg">{t.contact.cta}</div>
              <div className="text-[11px] uppercase tracking-widest font-bold opacity-80 mt-1">
                {t.contact.ctaSub}
              </div>
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, type = "text", value, onChange, required, testid }) => (
  <div>
    <label className="block text-xs font-bold uppercase tracking-widest text-brand-cream/65 mb-2">
      {label}
      {required && <span className="text-brand-red ml-1">*</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={testid}
      className="w-full rounded-full bg-brand-navy/40 border border-brand-cream/15 px-4 py-3 text-brand-cream placeholder-brand-cream/40 font-medium focus:outline-none focus:ring-2 focus:ring-brand-red/40 focus:border-brand-red/40 transition"
    />
  </div>
);

export default Contact;
